import { useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import * as R from 'remeda';
import { isDefined } from 'remeda';
import { z } from 'zod/v4';
import { type ApiError, addResourceNameToQueryResult, useAxiosInstance } from './utils';

const thirdPartyCompanySchema = z.object({
  id: z.number(),
  name: z.string().min(1),
});

// Debt collectors are a special case, because they have a boolean field `gcollect`; other than that they
// should be identical to any other third party company
const debtCollectorCompanySchema = z.object({
  id: z.number(),
  name: z.string().min(1),
  isGcollect: z.boolean(),
  isLegalCity: z.boolean(),
});

// Lawyers are a special case: LLJ requires AML compliance form on first case
const lawyerCompanySchema = z.object({
  id: z.number(),
  name: z.string().min(1),
  shouldShowAmlForm: z.boolean(),
});

const debtCollectorSchema = z
  .object({
    isAllowed: z.boolean(),
    reason: z.string().nullable(),
    availableCompanies: z.array(debtCollectorCompanySchema),
  })
  .transform(category => ({
    ...category,
    canBeAccessed: category.isAllowed && category.availableCompanies.length > 0,
  }));

const thirdPartyCategorySchema = z
  .object({
    isAllowed: z
      .boolean()
      .describe('Whether or not the status of the invoice permits using this type of third party'),
    reason: z
      .string()
      .nullable()
      .describe('Why this type is unavailable, as worded by the API; null when there is no reason'),
    availableCompanies: z
      .array(thirdPartyCompanySchema)
      .describe(
        'The array of third parties of this type that are available for the debtor/invoice country',
      ),
  })
  .transform(category => ({
    ...category,
    canBeAccessed: category.isAllowed && category.availableCompanies.length > 0,
  }));

export enum ThirdPartyType {
  BAILIFF = 'bailiff',
  DEBT_COLLECTOR = 'debtCollector',
  LAWYER = 'lawyer',
}

const lawyerSchema = z
  .object({
    isAllowed: z.boolean(),
    reason: z.string().nullable(),
    availableCompanies: z.array(lawyerCompanySchema),
  })
  .transform(category => ({
    ...category,
    canBeAccessed: category.isAllowed && category.availableCompanies.length > 0,
  }));

const thirdPartiesSchema = z
  .object({
    [ThirdPartyType.BAILIFF]: thirdPartyCategorySchema,
    [ThirdPartyType.LAWYER]: lawyerSchema,
    [ThirdPartyType.DEBT_COLLECTOR]: debtCollectorSchema,
  })
  .transform(thirdParties => ({
    ...thirdParties,
    areAnyThirdPartiesAccessible: R.pipe(
      thirdParties,
      Object.values,
      R.map(R.prop('canBeAccessed')),
      arr => arr.some(val => val),
    ),
  }));

export type ThirdParties = z.infer<typeof thirdPartiesSchema>;

type IdOrIds =
  | { id: number | undefined; ids?: never }
  | { ids: Array<number> | undefined; id?: never };
type LoadThirdPartiesVariables = { enabled?: boolean } & IdOrIds;

export async function loadThirdPartiesQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  { queryKey }: any,
) {
  const instance = await axiosInstance;

  const { data } = await instance.get('third_party_companies', {
    params: {
      [Array.isArray(queryKey[1]) ? 'invoice_ids' : 'invoice_id']: queryKey[1],
    },
  });
  return thirdPartiesSchema.parse(data);
}

const emptyThirdParties: ThirdParties = {
  [ThirdPartyType.BAILIFF]: {
    isAllowed: false,
    reason: null,
    availableCompanies: [],
    canBeAccessed: false,
  },
  [ThirdPartyType.LAWYER]: {
    isAllowed: false,
    reason: null,
    availableCompanies: [],
    canBeAccessed: false,
  },
  [ThirdPartyType.DEBT_COLLECTOR]: {
    isAllowed: false,
    reason: null,
    availableCompanies: [],
    canBeAccessed: false,
  },
  areAnyThirdPartiesAccessible: false,
};

export function useLoadThirdParties({ enabled = true, id, ids }: LoadThirdPartiesVariables) {
  const axiosInstance = useAxiosInstance();

  const queryResult = useQuery<ThirdParties, ApiError>({
    enabled,
    queryKey: ['thirdParty', id ?? ids],
    queryFn: context => loadThirdPartiesQueryFn(axiosInstance, context),
    placeholderData: previousData => {
      if (isDefined(previousData)) return previousData;
      return emptyThirdParties;
    },
  });

  return addResourceNameToQueryResult<ThirdParties, unknown, 'thirdParties', 'PreventUndefined'>(
    'thirdParties',
    queryResult,
    emptyThirdParties,
  );
}
